import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Globe, Lock, Church } from "lucide-react";
import { updateGroup, getMyParishesForInvite } from "@/lib/api";

export default function EditGroupModal({ isOpen, onClose, group, categories, onSuccess }) {
  const [name, setName] = useState(group?.name || "");
  const [description, setDescription] = useState(group?.description || "");
  const [category, setCategory] = useState(group?.category || "General");
  const [privacy, setPrivacy] = useState(group?.privacy || "public");
  const [loading, setLoading] = useState(false);
  const [userParishes, setUserParishes] = useState([]);
  const [selectedParishIds, setSelectedParishIds] = useState([]);

  useEffect(() => {
    if (group) {
      setName(group.name);
      setDescription(group.description);
      setCategory(group.category);
      setPrivacy(group.privacy);
      setSelectedParishIds(group.parish_only_parish_ids || []);
    }
  }, [group]);

  useEffect(() => {
    if (isOpen) {
      getMyParishesForInvite().then(res => setUserParishes(res.data.parishes || [])).catch(() => {});
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("Please enter a group name");
      return;
    }
    if (privacy === "parish_only" && selectedParishIds.length === 0) {
      toast.error("Please select at least one parish");
      return;
    }

    setLoading(true);
    try {
      const payload = { name, description, category, privacy };
      if (privacy === "parish_only") {
        payload.parish_only_parish_ids = selectedParishIds;
      }
      await updateGroup(group.group_id, payload);
      toast.success("Group updated!");
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update group");
    }
    setLoading(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26]">
            Edit Group
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#2D2A26] font-['Nunito']">Group Name</label>
            <Input 
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="border-[#E5E0D5] font-['Nunito']"
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#2D2A26] font-['Nunito']">Description</label>
            <Textarea 
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="border-[#E5E0D5] font-['Nunito'] min-h-[100px]"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#2D2A26] font-['Nunito']">Category</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="border-[#E5E0D5] font-['Nunito']">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-60">
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat} className="font-['Nunito']">{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#2D2A26] font-['Nunito']">Privacy</label>
              <Select 
                value={privacy} 
                onValueChange={(v) => {
                  setPrivacy(v);
                  if (v === "parish_only" && userParishes.length === 1) {
                    setSelectedParishIds([userParishes[0].parish_id]);
                  }
                }}
              >
                <SelectTrigger className="border-[#E5E0D5] font-['Nunito']" data-testid="edit-privacy-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="public" className="font-['Nunito']">
                    <span className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-[#556B2F]" />
                      Public
                    </span>
                  </SelectItem>
                  <SelectItem value="private" className="font-['Nunito']">
                    <span className="flex items-center gap-2">
                      <Lock className="w-4 h-4 text-[var(--liturgical-primary)]" />
                      Private
                    </span>
                  </SelectItem>
                  {userParishes.length > 0 && (
                    <SelectItem value="parish_only" className="font-['Nunito']">
                      <span className="flex items-center gap-2">
                        <Church className="w-4 h-4 text-[#4169E1]" />
                        Parish Only
                      </span>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <p className="text-xs text-[#6B665E] font-['Nunito']">
            {privacy === "public" 
              ? "Anyone can join this group" 
              : privacy === "private"
                ? "Members must be approved by an admin"
                : "Only members of the selected parish(es) can join"}
          </p>
          
          {/* Parish selector for parish_only */}
          {privacy === "parish_only" && userParishes.length > 1 && (
            <div className="space-y-2 p-3 bg-[#F0EBE0]/50 rounded-lg">
              <label className="text-sm font-medium text-[#2D2A26] font-['Nunito']">Select parishes that can join</label>
              <div className="space-y-1.5">
                {userParishes.map(p => (
                  <label key={p.parish_id} className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-white/60 transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedParishIds.includes(p.parish_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedParishIds([...selectedParishIds, p.parish_id]);
                        } else {
                          setSelectedParishIds(selectedParishIds.filter(id => id !== p.parish_id));
                        }
                      }}
                      className="rounded border-[#E5E0D5]"
                    />
                    <Church className="w-3.5 h-3.5 text-[#4169E1]" />
                    <span className="text-sm text-[#2D2A26] font-['Nunito']">{p.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#E5E0D5]">Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
